import { useQuery } from "@apollo/client";
import { MouseEvent, useCallback, useState } from "react";

import ActionIconButton from "@components/buttons/ActionIconButton";
import styles from "@components/PinToggle.module.css";
import useSubscribeToJourney from "@hooks/subscriptions/useSubscribeToJourney";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyClient from "@hooks/useMyClient";
import useMyExpert from "@hooks/useMyExpert";
import {
  CarePlanActionItemCtaEnum,
  GetPinsDocument,
  GetPinsQuery,
  GetPinsQueryVariables,
  Goal,
  PinResourceTypeEnum,
  PinToggleDocument,
  PinToggleMutation,
  PinToggleMutationVariables
} from "@typing/Generated";
import { Colors } from "@utils/colorUtils";
import { clickCurrentTarget } from "@utils/keyboardUtils";

type Props = {
  color?: Extract<Colors, "gray-500" | "white">;
  goal?: Pick<Goal, "hasDuplicateGoalInEditMode" | "id"> | null;
  journeyId: string;
  resourceId?: string;
  resourceType?: PinResourceTypeEnum;
  tool?: CarePlanActionItemCtaEnum;
};

const PinToggle = ({ color = "white", goal, journeyId, resourceId, resourceType, tool }: Props) => {
  const [loading, setLoading] = useState(false);

  const myClient = useMyClient();
  const myExpert = useMyExpert();

  const getPinsQuery = useQuery<GetPinsQuery, GetPinsQueryVariables>(GetPinsDocument, {
    variables: {
      journeyId
    }
  });

  useSubscribeToJourney(journeyId, () => {
    getPinsQuery.refetch();
  });

  const [pinToggle] = useMutationWithValidation<PinToggleMutation, PinToggleMutationVariables>(
    PinToggleDocument,
    "pins",
    {
      update: (store, { data }) => {
        const pinsCacheData = store.readQuery<GetPinsQuery, GetPinsQueryVariables>({
          query: GetPinsDocument,
          variables: { journeyId }
        });

        if (pinsCacheData) {
          if (data?.pinToggle?.pin) {
            const newCacheData = {
              ...pinsCacheData,
              pins: [...pinsCacheData.pins, data.pinToggle.pin]
            };

            store.writeQuery<GetPinsQuery, GetPinsQueryVariables>({
              data: newCacheData,
              query: GetPinsDocument,
              variables: { journeyId }
            });
          } else {
            const newPins = [
              ...pinsCacheData.pins.filter(
                pin => (tool && pin.tool !== tool) || (resourceId && pin.resource?.id !== resourceId)
              )
            ];

            const newCacheData = {
              ...pinsCacheData,
              pins: newPins
            };
            store.writeQuery<GetPinsQuery, GetPinsQueryVariables>({
              data: newCacheData,
              query: GetPinsDocument,
              variables: { journeyId }
            });
          }
        }
      }
    }
  );

  const pinned =
    getPinsQuery.data?.pins.some(
      pin => (resourceId && pin.resource?.id === resourceId) || (tool && pin.tool === tool)
    ) ?? false;

  const handleSaveToggle = useCallback(
    (event: MouseEvent<HTMLDivElement>, pinned: boolean) => {
      setLoading(true);
      event.preventDefault();
      event.stopPropagation();
      pinToggle({
        goalId: goal?.id,
        journeyId,
        pinned,
        resourceId,
        resourceType,
        tool
      }).then(() => {
        setLoading(false);
      });
    },
    [goal?.id, journeyId, pinToggle, resourceId, resourceType, tool]
  );

  if ((!myClient && !myExpert) || getPinsQuery.loading || goal?.hasDuplicateGoalInEditMode) {
    return null;
  }

  return (
    <div
      className={`${pinned ? styles.saved : styles.notSaved} ${styles.withoutText} ${styles[color]}`}
      data-test={pinned ? "pinned" : "not-pinned"}
      onClick={event => (loading ? {} : handleSaveToggle(event, !pinned))}
      onKeyUp={clickCurrentTarget}
      role="button"
      tabIndex={0}
    >
      <ActionIconButton ariaLabelKey="accessibility.ariaLabels.pin" icon="thumbtack" />
    </div>
  );
};

export default PinToggle;
